/** The signed-out endpoints, as they will exist — standing in for them.
 *
 *  STAGE A. Same contract as the module fixtures: these are the shapes the
 *  Phase 1 backend gets WRITTEN TO RETURN, so swapping to live data is one
 *  line per call and nothing above it changes.
 *
 *  Ported from Fabric `backend/ui/public/routes.py` + the MFA half of
 *  `backend/ui/auth/routes.py`. Behaviours kept deliberately, not copied
 *  carelessly:
 *
 *  - **SEC-01 (forgot-password) is a NON-ANSWER, always.** The response is
 *    identical whether or not the address has an account, because the
 *    difference between "sent" and "no such user" is an account-existence
 *    oracle. Fabric also EQUALISES RESPONSE TIME on this path
 *    (`email_service.py:30`) — a fast "no such user" leaks the same fact
 *    through the clock that the wording no longer leaks. The UI cannot fix
 *    that and must not add a fast path of its own: it always awaits the call.
 *  - **The three token failures are the only three.** `TOKEN_EXPIRED`,
 *    `TOKEN_INVALID`, `PASSWORD_POLICY` — Fabric's `_humanise` map exactly.
 *    An unmapped code is logged and shown as a sentence, never printed raw,
 *    the same rule LoginPage follows.
 *  - **A download token IS the credential.** No session. Eligibility is
 *    re-checked and the build re-resolved AT CLICK TIME, so a link goes stale
 *    the moment access or the release lineup changes. 24 h, capped multi-use.
 *
 *  KNOWN, PORTED AS-IS, FLAGGED FOR STAGE C: these links carry their token in
 *  the URL. A URL reaches browser history, `Referer` on any outbound link, and
 *  every proxy log in between. Fabric does it this way and stage A does not
 *  redesign it — but it is a real exposure, not a style preference, and it is
 *  the kind of thing the per-route gate exists to catch.
 */

/** Fabric's `_humanise` — the three token failures, verbatim in meaning. */
export const TOKEN_ERRORS: Record<string, string> = {
  TOKEN_EXPIRED: "That link has expired. Please request a new one.",
  TOKEN_INVALID: "That link is invalid or has already been used.",
};

/** Fabric's `_humanise_download` — a download link fails differently: it can
 *  also be exhausted, and the person's fix is an administrator, not a retry. */
export const DOWNLOAD_ERRORS: Record<string, string> = {
  TOKEN_EXPIRED:
    "This download link has expired — links are valid for 24 hours. " +
    "Ask your administrator to send a new one.",
  TOKEN_INVALID:
    "This download link is invalid or has reached its download limit. " +
    "Ask your administrator to send a new one.",
};

export function humanise(code: string, message: string | undefined, map: Record<string, string>): string {
  if (code === "PASSWORD_POLICY" && message) return message;   // the server owns this text
  if (!map[code]) console.warn("unmapped public error code:", code);
  return map[code] ?? "That link cannot be used. Ask your administrator for a new one.";
}

/* ------------------------------------------------------------------ *
 * THE REAL ENDPOINTS. These were STANDINS until 2026-09-29 — six functions
 * that slept and returned a hardcoded answer, with a comment promising
 * "each becomes one api.post(...) in Phase 1".
 *
 * THE STANDINS WERE WORSE THAN UNFINISHED, and it is worth saying how,
 * because the shape recurs: `resetPassword` RETURNED SUCCESS for any token
 * except the literal string "expired". A person clicking a real reset link
 * saw it work, was told their password was set, and it never was. Nothing
 * failed, nothing logged, and the only symptom was being unable to sign in
 * afterwards with the password you had just chosen.
 *
 * So the admin half depends on this half being real: every "send link"
 * button in the console is a link that lands here.
 * ------------------------------------------------------------------ */

import { api, ApiError } from "@/api/client";

/** Where the signed-out endpoints live. Orbit's own identity (`/auth/v2`),
 *  not Fabric's — the console switched over in the cutover. */
const A = "/auth/v2";

/** Always resolves. Never reports whether the address existed — SEC-01.
 *
 *  THE NON-ANSWER IS THE SERVER'S, and this must not add a fast path of its
 *  own: the route deliberately pays for one password verify on the miss
 *  branch so response TIME does not leak what the wording refuses to. A
 *  caller that short-circuited on a client-side guess would hand that back.
 */
export async function forgotPassword(loginId: string): Promise<void> {
  await api.post(`${A}/forgot-password`, { login_id: loginId });
}

/** Token + new password. Throws `ApiError` carrying one of the three codes. */
export async function resetPassword(token: string, password: string): Promise<{ is_admin: boolean }> {
  await api.post(`${A}/reset-password`, { token, new_password: password });
  // The server ends every session on a reset, so there is nobody to be an
  // admin OF yet — the page sends them to sign in either way.
  return { is_admin: false };
}

export async function verifyEmail(token: string): Promise<{ is_admin: boolean }> {
  await api.post(`${A}/verify-email`, { token });
  return { is_admin: false };
}

export type DownloadTarget = { filename: string; product: string; version: string; size_bytes: number };

/** RESOLVE ONLY — this does not spend one of the link's uses.
 *
 *  The page shows what is about to land on the machine before a
 *  hundreds-of-megabyte executable starts arriving. Charging the cap for a
 *  page load would let a mail scanner's preview exhaust the link by looking
 *  at it, which is the exact failure the cap exists to prevent.
 */
export async function redeemDownload(token: string): Promise<DownloadTarget> {
  const r = await api.get<{ product: string; tool: string; version: string;
    filename: string; size_bytes: number; sha256: string }>(
    `${A}/tool-download/${encodeURIComponent(token)}`);
  return { filename: r.filename, product: `${r.product} ${r.tool}`,
           version: r.version, size_bytes: r.size_bytes };
}

/** Where the BYTES come from. A separate request, and the one that spends a
 *  use — eligibility is re-checked there too, because the answer can change
 *  between resolving and downloading. */
export function downloadHref(token: string): string {
  return `${A}/tool-download/${encodeURIComponent(token)}/file`;
}

/* ---------------------------- MFA enrolment ---------------------------- */

export type EnrolStart = { secret: string; otpauth_uri: string; qr_png_path: string };

/** Offer a candidate secret. NOTHING is stored until a code proves it, which
 *  is why a reload is safe: the pending secret lives in the page, and the
 *  server has not keyed anything to it yet. */
export async function startEnrol(_loginId: string): Promise<EnrolStart> {
  return api.post<EnrolStart>(`${A}/mfa/begin`, {});
}

/** Verify the first code, store the secret, return the backup codes ONCE.
 *
 *  THE PROOF IS SERVER-SIDE AND SINGLE-USE, and this call can legitimately
 *  refuse: `finish-enrol` was once reachable from a password-only session and
 *  granted a full admin session with no code ever verified
 *  (`tests/security/test_mfa_finish_enrol_bypass.py`). So the UI must never
 *  treat "I reached the backup-codes screen" as proof of enrolment.
 */
export async function verifyEnrol(secret: string, code: string): Promise<{ backup_codes: string[] }> {
  return api.post<{ backup_codes: string[] }>(`${A}/mfa/finish`, { secret, code });
}

export { ApiError };
